import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useIsAdmin } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldAlert, Save, ArrowLeft, ExternalLink, Trash2, AlertCircle, Wand2 } from "lucide-react";

/**
 * /ad/marketing/jobs/:jobId/edit — Full-fidelity admin editor for a job posting.
 *
 * Mirrors AdminCoachEdit.jsx: sectioned structured inputs for the ~30 common
 * fields, JSON editors for nested sub-docs (facilities, camps_clinics, needs).
 * PUTs to /api/admin/jobs/{id}/full with server-side allow-list enforcement.
 */
export default function AdminJobEdit({ isNew: isNewProp }) {
  const { jobId } = useParams();
  const isNew = !!isNewProp || jobId === "new";
  const isAdmin = useIsAdmin();
  const nav = useNavigate();

  const [orig, setOrig] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jsonErrors, setJsonErrors] = useState({});
  const [prefillUrl, setPrefillUrl] = useState("");
  const [prefilling, setPrefilling] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    if (isNew) {
      // Blank template — matches the shape the server will insert.
      const blank = {
        title: "",
        program_name: "",
        description: "",
        sport: "Lacrosse",
        gender: "Men",
        level: "HS",
        division: "",
        role: "Head Coach",
        location: "",
        city: "",
        state: "",
        job_type: "Full Time",
        status: "draft",
        anonymous_listing: false,
        salary_band: "",
        salary_min: null,
        salary_max: null,
        needs: {},
        facilities: {},
        camps_clinics: {},
        faculty_subjects_needed: [],
        is_faculty_position: "none",
      };
      // orig stays null so every field is considered dirty on create
      setOrig({});
      setForm(blank);
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/jobs/${jobId}`)
      .then(({ data }) => { setOrig(data); setForm(data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [jobId, isAdmin, isNew]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const [compIndex, setCompIndex] = useState(null);
  const [indexBusy, setIndexBusy] = useState(false);
  const cix = form?.comp_index_data || {};
  const cixBen = cix.benefits || {};
  const setCix = (k, v) => set("comp_index_data", { ...cix, [k]: v });
  const setCixBen = (k, v) => set("comp_index_data", { ...cix, benefits: { ...cixBen, [k]: v } });
  const computeIndex = async () => {
    setIndexBusy(true);
    try {
      const { data } = await api.get(`/admin/jobs/${jobId}/comp-index`);
      setCompIndex(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Index computation failed");
    } finally { setIndexBusy(false); }
  };
  const setNested = (parent, key, value) =>
    setForm((f) => ({ ...f, [parent]: { ...(f?.[parent] || {}), [key]: value } }));

  const dirty = useMemo(() => {
    if (!orig || !form) return false;
    return JSON.stringify(orig) !== JSON.stringify(form);
  }, [orig, form]);

  const prefill = async () => {
    if (!prefillUrl.startsWith("http")) {
      toast.error("Paste a full URL starting with http(s)://");
      return;
    }
    setPrefilling(true);
    try {
      const { data } = await api.post("/admin/jobs/prefill-from-url", { url: prefillUrl });
      setForm((f) => ({ ...(f || {}), ...(data.prefill || {}) }));
      toast.success(`Auto-filled ${data.fields_extracted} field${data.fields_extracted === 1 ? "" : "s"} — review + tweak below.`);
      setPrefillUrl("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Prefill failed");
    } finally {
      setPrefilling(false);
    }
  };

  const save = async () => {
    if (Object.keys(jsonErrors).length) {
      toast.error("Fix JSON errors before saving");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        // Strip empties so we don't insert empty strings for optional fields
        const payload = {};
        Object.keys(form).forEach((k) => {
          const v = form[k];
          if (v === null || v === "" || (Array.isArray(v) && !v.length)) return;
          payload[k] = v;
        });
        if (!payload.title || !payload.title.trim()) {
          toast.error("Title is required");
          setSaving(false);
          return;
        }
        const { data } = await api.post(`/admin/jobs/full`, payload);
        toast.success("Job created — you can keep editing.");
        nav(`/ad/marketing/jobs/${data.job.id}/edit`, { replace: true });
        return;
      }
      const payload = {};
      Object.keys(form).forEach((k) => {
        if (JSON.stringify(form[k]) !== JSON.stringify(orig?.[k])) payload[k] = form[k];
      });
      if (!Object.keys(payload).length) {
        toast.info("Nothing changed");
        setSaving(false);
        return;
      }
      const { data } = await api.put(`/admin/jobs/${jobId}/full`, payload);
      setOrig(data.job);
      setForm(data.job);
      toast.success(
        `Saved · ${Object.keys(payload).length} field${Object.keys(payload).length === 1 ? "" : "s"} updated` +
        (data.dropped_fields?.length ? ` · ${data.dropped_fields.length} protected field(s) ignored` : "")
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return <Shell><div className="p-10 border border-blaze text-blaze" data-testid="admin-job-edit-forbidden">Platform-admin only.</div></Shell>;
  if (loading || !form) return <Shell><div className="p-10 heading text-2xl">LOADING…</div></Shell>;

  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="admin-job-edit-page">
        <div className="flex items-center gap-2 text-xs tracking-widest text-volt heading">
          <ShieldAlert size={14} /> PLATFORM ADMIN · {isNew ? "NEW JOB (ADMIN-AUTHORED)" : "FULL JOB EDITOR"}
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-2">
          <div>
            <h1 className="heading text-3xl md:text-4xl">{isNew ? "CREATE NEW JOB" : (form.title || "(untitled job)")}</h1>
            <div className="text-neutral-500 text-xs mt-1">
              {isNew ? (
                <span>Starts as <span className="text-neutral-300">draft</span> — flip Status to <span className="text-volt">open</span> when ready to publish.</span>
              ) : (
                <>
                  {form.program_name && <span>{form.program_name} · </span>}
                  id <code className="text-neutral-300">{form.id}</code>
                  {form.last_admin_edit_by && (
                    <span className="ml-3">last edited by <span className="text-neutral-300">{form.last_admin_edit_by}</span> · {form.last_admin_edit_at?.slice(0, 16)?.replace("T", " ")}</span>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isNew && (
              <Link to={`/jobs/${jobId}`} data-testid="admin-job-edit-view-public" className="text-xs heading tracking-widest border border-neutral-800 px-3 py-2 hover:border-volt hover:text-volt flex items-center gap-1">
                <ExternalLink size={12} /> VIEW PUBLIC
              </Link>
            )}
            <button onClick={() => nav(-1)} data-testid="admin-job-edit-back-btn" className="text-xs heading tracking-widest border border-neutral-800 px-3 py-2 hover:border-volt hover:text-volt flex items-center gap-1">
              <ArrowLeft size={12} /> BACK
            </button>
          </div>
        </div>

        {/* Sticky save bar */}
        <div className={`sticky top-0 z-30 -mx-4 md:-mx-8 mt-6 px-4 md:px-8 py-3 flex items-center justify-between backdrop-blur-md border-b ${(dirty || isNew) ? "border-volt bg-[#0A101A]/95" : "border-neutral-900 bg-black/60"}`}>
          <div className="text-xs heading tracking-widest">
            {isNew ? <span className="text-volt">● NEW · UNSAVED</span> : dirty ? <span className="text-volt">● UNSAVED CHANGES</span> : <span className="text-neutral-500">ALL SAVED</span>}
            {Object.keys(jsonErrors).length > 0 && (
              <span className="ml-3 text-blaze flex items-center gap-1 inline-flex"><AlertCircle size={12} /> {Object.keys(jsonErrors).length} JSON error(s)</span>
            )}
          </div>
          <button
            onClick={save}
            disabled={(!dirty && !isNew) || saving || Object.keys(jsonErrors).length > 0}
            data-testid="admin-job-edit-save-btn"
            className="bg-blaze text-white heading text-sm tracking-widest px-5 py-2.5 hover:glow-blaze disabled:opacity-40 flex items-center gap-2"
          >
            <Save size={14} /> {saving ? (isNew ? "CREATING…" : "SAVING…") : (isNew ? "CREATE JOB" : "SAVE ALL CHANGES")}
          </button>
        </div>

        {/* AI Prefill from URL — only offered in NEW mode */}
        {isNew && (
          <div className="mt-6 border border-volt/40 bg-[#0F1420] p-5" data-testid="admin-job-prefill-panel">
            <div className="flex items-center gap-2 text-xs tracking-widest text-volt heading">
              <Wand2 size={14} /> AI PREFILL FROM URL
            </div>
            <p className="text-neutral-400 text-xs mt-1 max-w-2xl">
              Paste a link to an athletic-department job posting or a job board listing. Claude will read the page and auto-fill everything it can identify. Review + tweak before hitting <span className="text-volt">CREATE JOB</span>.
            </p>
            <div className="mt-3 flex flex-col md:flex-row gap-2">
              <input
                data-testid="admin-job-prefill-url"
                value={prefillUrl}
                onChange={(e) => setPrefillUrl(e.target.value)}
                placeholder="https://schoolathletics.com/sports/.../job-posting"
                className="flex-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
              />
              <button
                onClick={prefill}
                disabled={prefilling || !prefillUrl}
                data-testid="admin-job-prefill-btn"
                className="bg-volt text-white heading text-xs tracking-widest px-5 py-2.5 hover:glow-volt disabled:opacity-40 flex items-center gap-2 whitespace-nowrap"
              >
                <Wand2 size={14} /> {prefilling ? "READING…" : "AUTO-FILL"}
              </button>
            </div>
          </div>
        )}

        {/* Listing copy */}
        <Section title="Listing Copy" testid="section-listing-copy">
          <Grid>
            <TextField label="Title" v={form.title} onChange={(v) => set("title", v)} tid="edit-title" wide />
            <TextField label="Anonymized Title (public if anon)" v={form.anonymized_title} onChange={(v) => set("anonymized_title", v)} tid="edit-anonymized_title" wide />
            <TextField label="Program / School Name" v={form.program_name} onChange={(v) => set("program_name", v)} tid="edit-program_name" wide />
            <BoolField label="Anonymous Listing (mask program)" v={form.anonymous_listing} onChange={(v) => set("anonymous_listing", v)} tid="edit-anonymous_listing" />
            <SelectField label="Status" v={form.status} options={["", "open", "closed", "draft", "deleted"]} onChange={(v) => set("status", v)} tid="edit-status" />
            <TextAreaField label="Description" v={form.description} onChange={(v) => set("description", v)} tid="edit-description" rows={8} />
          </Grid>
        </Section>

        {/* Taxonomy */}
        <Section title="Sport / Level / Role" testid="section-taxonomy">
          <Grid>
            <SelectField label="Sport" v={form.sport} options={["Lacrosse", "Football", "Basketball", "Baseball", "Soccer", "Volleyball"]} onChange={(v) => set("sport", v)} tid="edit-sport" />
            <SelectField label="Gender" v={form.gender} options={["Men", "Women", "Coed"]} onChange={(v) => set("gender", v)} tid="edit-gender" />
            <SelectField label="Level" v={form.level} options={["MS", "HS", "JUCO", "College"]} onChange={(v) => set("level", v)} tid="edit-level" />
            <SelectField label="Division" v={form.division} options={["", "D1", "D2", "D3", "NAIA", "NJCAA", "MCLA D1", "MCLA D2", "WCLA", "NCLA", "Club", "HS Varsity", "JV", "Freshman", "Middle School"]} onChange={(v) => set("division", v)} tid="edit-division" />
            <TextField label="Conference" v={form.conference} onChange={(v) => set("conference", v)} tid="edit-conference" />
            <TextField label="Role (Head Coach, Assistant, …)" v={form.role} onChange={(v) => set("role", v)} tid="edit-role" />
            <TextField label="Setting (urban / suburban / rural)" v={form.setting} onChange={(v) => set("setting", v)} tid="edit-setting" />
            <TextField label="Selectivity" v={form.selectivity} onChange={(v) => set("selectivity", v)} tid="edit-selectivity" />
            <TextField label="Enrollment" v={form.enrollment} onChange={(v) => set("enrollment", v)} tid="edit-enrollment" />
            <TextField label="Scholarship Type" v={form.scholarship_type} onChange={(v) => set("scholarship_type", v)} tid="edit-scholarship_type" />
            <div>
              <label className="text-[10px] tracking-widest text-neutral-500">NIL AVAILABLE</label>
              <label className="flex items-center gap-2 mt-1.5 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="edit-nil_available"
                  checked={!!form.nil_available}
                  onChange={(e) => set("nil_available", e.target.checked)}
                  className="w-4 h-4 accent-volt cursor-pointer"
                />
                <span className="text-neutral-300">Program offers NIL money / brand deals</span>
              </label>
            </div>
            <TextField label="Region" v={form.region} onChange={(v) => set("region", v)} tid="edit-region" />
          </Grid>
        </Section>

        {/* Hiring contact — platform-admin-only */}
        <Section title="Hiring Contact · Admin Only" testid="section-admin-contact">
          <p className="text-xs text-neutral-500 mb-3">
            Never shown publicly. Contact details found embedded in scraped descriptions are moved here automatically.
          </p>
          <Grid>
            <TextField label="Contact Name" v={form.admin_contact_name} onChange={(v) => set("admin_contact_name", v)} tid="edit-admin_contact_name" />
            <TextField label="Contact Email" v={form.admin_contact_email} onChange={(v) => set("admin_contact_email", v)} tid="edit-admin_contact_email" />
            <TextField label="Contact Phone" v={form.admin_contact_phone} onChange={(v) => set("admin_contact_phone", v)} tid="edit-admin_contact_phone" />
            <TextField label="Original Contact Text (as scraped)" v={form.admin_contact_raw} onChange={(v) => set("admin_contact_raw", v)} tid="edit-admin_contact_raw" wide />
          </Grid>
        </Section>

        {/* Compensation Index inputs — platform-admin-only */}
        <Section title="Compensation Index Data · Admin Only" testid="section-comp-index">
          <p className="text-xs text-neutral-500 mb-3">
            Feeds the CoachConnect Compensation Index — Guaranteed Compensation 35% · Total Financial Value 20% · Program/Career Value 20% · Cost of Living 15% · Family &amp; QoL 10%. Never shown publicly.
          </p>
          <Grid>
            <NumberField label="Coach Salary Min ($)" v={cix.coach_salary_min} onChange={(v) => setCix("coach_salary_min", v)} tid="edit-cix-salary-min" />
            <NumberField label="Coach Salary Max ($)" v={cix.coach_salary_max} onChange={(v) => setCix("coach_salary_max", v)} tid="edit-cix-salary-max" />
            <NumberField label="Additional Duties Salary ($)" v={cix.additional_duties_salary} onChange={(v) => setCix("additional_duties_salary", v)} tid="edit-cix-add-duties" />
            <NumberField label="Retirement Contribution (% match, 401k/403b)" v={cix.retirement_pct} step="0.5" onChange={(v) => setCix("retirement_pct", v)} tid="edit-cix-retirement" />
            <BoolField label="Healthcare" v={!!cixBen.healthcare} onChange={(v) => setCixBen("healthcare", v)} tid="edit-cix-healthcare" />
            <BoolField label="Dental" v={!!cixBen.dental} onChange={(v) => setCixBen("dental", v)} tid="edit-cix-dental" />
            <BoolField label="Vision" v={!!cixBen.vision} onChange={(v) => setCixBen("vision", v)} tid="edit-cix-vision" />
            <BoolField label="Tuition for Grad Degree" v={!!cix.tuition_grad} onChange={(v) => setCix("tuition_grad", v)} tid="edit-cix-tuition" />
            <BoolField label="Housing Provided / Subsidized" v={!!cix.housing} onChange={(v) => setCix("housing", v)} tid="edit-cix-housing" />
            <BoolField label="Dedicated Field for Camps/Clinics (no cost)" v={!!cix.field_access} onChange={(v) => setCix("field_access", v)} tid="edit-cix-field" />
            <NumberField label="Career Value Override (0-100, optional)" v={cix.career_value_score} onChange={(v) => setCix("career_value_score", v)} tid="edit-cix-career" />
            <TextField label="Other Perks / Notes" v={cix.other} onChange={(v) => setCix("other", v)} tid="edit-cix-other" wide />
          </Grid>
          {!isNew && (
            <div className="mt-4">
              <button
                onClick={computeIndex}
                disabled={indexBusy}
                data-testid="compute-comp-index-btn"
                className="text-xs tracking-widest heading border border-volt text-volt px-3 py-2 hover:bg-volt hover:text-white transition-colors disabled:opacity-50"
              >
                {indexBusy ? "SCORING…" : "COMPUTE COMPENSATION INDEX"}
              </button>
              <p className="text-[10px] text-neutral-600 mt-1">Save first — the index scores what's stored on the job.</p>
              {compIndex && (
                <div className="mt-4 border border-neutral-800 p-4" data-testid="comp-index-result">
                  <div className="flex items-baseline gap-3">
                    <span className="heading text-4xl" style={{ color: "#FF3B30" }}>{compIndex.index ?? "—"}</span>
                    <span className="text-[10px] tracking-widest text-neutral-400">COACHCONNECT COMPENSATION INDEX / 100</span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {compIndex.factors.map((f) => (
                      <div key={f.key} className="flex items-center gap-3">
                        <span className="text-[10px] tracking-widest text-neutral-400 w-52 shrink-0">{f.label.toUpperCase()} · {f.weight}%</span>
                        <div className="flex-1 h-2 bg-neutral-900">
                          <div className="h-2" style={{ width: `${f.score ?? 0}%`, backgroundColor: f.score == null ? "#333" : "#FF3B30" }} />
                        </div>
                        <span className="text-xs font-semibold w-8 text-right">{f.score ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                  {compIndex.missing_data.length > 0 && (
                    <p className="text-[10px] text-yellow-600 mt-3">MISSING DATA: {compIndex.missing_data.join(", ")} — score uses available factors only.</p>
                  )}
                  {compIndex.benchmark_band && (
                    <p className="text-[10px] text-neutral-500 mt-1">Benchmarked as {compIndex.position} vs ${(compIndex.benchmark_band[0] / 1000)}K–${(compIndex.benchmark_band[1] / 1000)}K band{compIndex.state ? ` · COL state: ${compIndex.state}` : ""}.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Location */}
        <Section title="Location" testid="section-location">
          <Grid>
            <TextField label="Location (freeform)" v={form.location} onChange={(v) => set("location", v)} tid="edit-location" wide />
            <TextField label="City" v={form.city} onChange={(v) => set("city", v)} tid="edit-city" />
            <TextField label="State" v={form.state} onChange={(v) => set("state", v)} tid="edit-state" />
          </Grid>
        </Section>

        {/* Comp + logistics */}
        <Section title="Compensation & Logistics" testid="section-comp">
          <Grid>
            <SelectField label="Compensation Type" v={form.compensation_type} options={["", "Full-Time Salary", "Part-Time Stipend", "Volunteer", "Commensurate"]} onChange={(v) => set("compensation_type", v)} tid="edit-compensation_type" />
            <TextField label="Stipend (part-time roles)" v={form.stipend} onChange={(v) => set("stipend", v)} tid="edit-stipend" />
            <TextField label="Salary Band (display)" v={form.salary_band} onChange={(v) => set("salary_band", v)} tid="edit-salary_band" />
            <NumberField label="Salary Min ($)" v={form.salary_min} onChange={(v) => set("salary_min", v)} tid="edit-salary_min" />
            <NumberField label="Salary Max ($)" v={form.salary_max} onChange={(v) => set("salary_max", v)} tid="edit-salary_max" />
            <TextField label="Job Type" v={form.job_type} onChange={(v) => set("job_type", v)} tid="edit-job_type" />
            <TextField label="Months" v={form.months} onChange={(v) => set("months", v)} tid="edit-months" />
            <TextField label="Hours" v={form.hours} onChange={(v) => set("hours", v)} tid="edit-hours" />
            <TextField label="Application Deadline" v={form.application_deadline} onChange={(v) => set("application_deadline", v)} tid="edit-application_deadline" />
            <TextField label="Start Date" v={form.start_date} onChange={(v) => set("start_date", v)} tid="edit-start_date" />
            <TextField label="Application URL" v={form.application_url} onChange={(v) => set("application_url", v)} tid="edit-application_url" wide />
            <TextField label="Contact Name" v={form.contact_name} onChange={(v) => set("contact_name", v)} tid="edit-contact_name" />
            <TextField label="Contact Email" v={form.contact_email} onChange={(v) => set("contact_email", v)} tid="edit-contact_email" />
            <TextField label="Contact Phone" v={form.contact_phone} onChange={(v) => set("contact_phone", v)} tid="edit-contact_phone" />
          </Grid>
        </Section>

        {/* HS-only faculty */}
        <Section title="HS Faculty Hybrid (HS-level only)" testid="section-faculty" subtitle="Signals for coach+teacher hybrid roles. Ignored at college level.">
          <Grid>
            <SelectField label="Faculty Position" v={form.is_faculty_position} options={["", "none", "preferred", "required"]} onChange={(v) => set("is_faculty_position", v)} tid="edit-is_faculty_position" />
            <StringListField label="Faculty Subjects Needed" v={form.faculty_subjects_needed} onChange={(v) => set("faculty_subjects_needed", v)} tid="edit-faculty_subjects_needed" wide />
          </Grid>
        </Section>

        {/* Source tracking */}
        <Section title="Source Tracking" testid="section-source">
          <Grid>
            <TextField label="Source URL (scraper origin)" v={form.source_url} onChange={(v) => set("source_url", v)} tid="edit-source_url" wide />
            <TextField label="Posting Source (usalacrosse, usila, hand-entered, …)" v={form.posting_source} onChange={(v) => set("posting_source", v)} tid="edit-posting_source" wide />
          </Grid>
        </Section>

        {/* Program needs */}
        <Section title="Program Needs" testid="section-needs" subtitle="Qualitative signals about culture, resources, challenges — powers matching narrative.">
          <Grid>
            <TextField label="Program Status" v={form.needs?.program_status} onChange={(v) => setNested("needs", "program_status", v)} tid="edit-needs_program_status" />
            <TextField label="Philosophy Priority" v={form.needs?.philosophy_priority} onChange={(v) => setNested("needs", "philosophy_priority", v)} tid="edit-needs_philosophy_priority" />
            <TextField label="Cultural Fit" v={form.needs?.cultural_fit} onChange={(v) => setNested("needs", "cultural_fit", v)} tid="edit-needs_cultural_fit" />
            <TextField label="Academic Focus" v={form.needs?.academic_focus} onChange={(v) => setNested("needs", "academic_focus", v)} tid="edit-needs_academic_focus" />
            <TextAreaField label="Resources" v={form.needs?.resources} onChange={(v) => setNested("needs", "resources", v)} tid="edit-needs_resources" rows={3} />
            <TextAreaField label="Challenges" v={form.needs?.challenges} onChange={(v) => setNested("needs", "challenges", v)} tid="edit-needs_challenges" rows={3} />
            <TextAreaField label="Extra Notes" v={form.needs?.extra_notes} onChange={(v) => setNested("needs", "extra_notes", v)} tid="edit-needs_extra_notes" rows={3} />
          </Grid>
        </Section>

        {/* Facilities */}
        <Section title="Facilities" testid="section-facilities">
          <Grid>
            <TextField label="Practice Field (turf)" v={form.facilities?.practice_field_turf} onChange={(v) => setNested("facilities", "practice_field_turf", v)} tid="edit-fac_practice_turf" />
            <TextField label="Practice Field (grass)" v={form.facilities?.practice_field_grass} onChange={(v) => setNested("facilities", "practice_field_grass", v)} tid="edit-fac_practice_grass" />
            <TextField label="Game Field (turf)" v={form.facilities?.game_field_turf} onChange={(v) => setNested("facilities", "game_field_turf", v)} tid="edit-fac_game_turf" />
            <TextField label="Game Field (grass)" v={form.facilities?.game_field_grass} onChange={(v) => setNested("facilities", "game_field_grass", v)} tid="edit-fac_game_grass" />
            <BoolField label="Indoor Facility" v={form.facilities?.indoor_facility} onChange={(v) => setNested("facilities", "indoor_facility", v)} tid="edit-fac_indoor" />
            <TextField label="Indoor Facility Notes" v={form.facilities?.indoor_facility_notes} onChange={(v) => setNested("facilities", "indoor_facility_notes", v)} tid="edit-fac_indoor_notes" />
            <TextField label="Weight Room" v={form.facilities?.weight_room} onChange={(v) => setNested("facilities", "weight_room", v)} tid="edit-fac_weight" />
            <TextField label="Locker Room" v={form.facilities?.locker_room} onChange={(v) => setNested("facilities", "locker_room", v)} tid="edit-fac_locker" />
            <TextField label="Other Facilities" v={form.facilities?.other_facilities} onChange={(v) => setNested("facilities", "other_facilities", v)} tid="edit-fac_other" wide />
          </Grid>
        </Section>

        {/* Camps */}
        <Section title="Camps & Clinics" testid="section-camps">
          <Grid>
            <TextField label="Fields" v={form.camps_clinics?.fields} onChange={(v) => setNested("camps_clinics", "fields", v)} tid="edit-camps_fields" />
            <TextField label="Dorms" v={form.camps_clinics?.dorms} onChange={(v) => setNested("camps_clinics", "dorms", v)} tid="edit-camps_dorms" />
          </Grid>
        </Section>

        {/* Advanced raw JSON escape hatch */}
        <Section title="Advanced · Raw Sub-Documents" testid="section-advanced" subtitle="Anything unusual → edit as JSON. Live-validated before save.">
          <div className="text-[10px] tracking-widest text-neutral-500 mb-2">FACILITIES (JSON)</div>
          <JsonField
            v={form.facilities}
            onChange={(v) => set("facilities", v)}
            onError={(err) => setJsonErrors((e) => ({ ...e, facilities_raw: err }))}
            onValid={() => setJsonErrors((e) => { const c = { ...e }; delete c.facilities_raw; return c; })}
            tid="edit-facilities-raw"
            rows={6}
          />
          <div className="text-[10px] tracking-widest text-neutral-500 mt-4 mb-2">NEEDS (JSON)</div>
          <JsonField
            v={form.needs}
            onChange={(v) => set("needs", v)}
            onError={(err) => setJsonErrors((e) => ({ ...e, needs_raw: err }))}
            onValid={() => setJsonErrors((e) => { const c = { ...e }; delete c.needs_raw; return c; })}
            tid="edit-needs-raw"
            rows={6}
          />
        </Section>

        {/* Danger zone */}
        {!isNew && (
          <Section title="Danger Zone" testid="section-danger" danger>
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="admin-job-edit-soft-delete-btn"
              onClick={async () => {
                if (!window.confirm(`Soft-delete this job? It will be hidden from every listing.`)) return;
                try {
                  await api.delete(`/admin/jobs/${jobId}`);
                  toast.success("Job soft-deleted");
                  nav("/ad");
                } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
              }}
              className="border border-blaze text-blaze text-xs heading tracking-widest px-4 py-2 hover:bg-blaze hover:text-white flex items-center gap-1"
            >
              <Trash2 size={12} /> SOFT DELETE
            </button>
            <button
              data-testid="admin-job-edit-hard-delete-btn"
              onClick={async () => {
                const phrase = window.prompt("Type DELETE to permanently remove this job:");
                if (phrase !== "DELETE") { toast.error("Cancelled"); return; }
                try {
                  await api.delete(`/admin/jobs/${jobId}?hard=true`);
                  toast.success("Job permanently deleted");
                  nav("/ad");
                } catch (e) { toast.error(e.response?.data?.detail || "Hard delete failed"); }
              }}
              className="text-xs heading tracking-widest text-red-500 hover:text-red-400 px-4 py-2 flex items-center gap-1"
            >
              <Trash2 size={12} /> HARD DELETE (PERMANENT)
            </button>
          </div>
        </Section>
        )}

        <div className="mt-8 flex justify-end">
          <button
            onClick={save}
            disabled={(!dirty && !isNew) || saving || Object.keys(jsonErrors).length > 0}
            data-testid="admin-job-edit-save-btn-bottom"
            className="bg-blaze text-white heading text-sm tracking-widest px-6 py-3 hover:glow-blaze disabled:opacity-40 flex items-center gap-2"
          >
            <Save size={14} /> {saving ? (isNew ? "CREATING…" : "SAVING…") : (isNew ? "CREATE JOB" : "SAVE ALL CHANGES")}
          </button>
        </div>
      </div>
    </Shell>
  );
}

// ---- Layout + field primitives (duplicated from AdminCoachEdit for isolation) ----

function Shell({ children }) {
  return <div className="text-white">{children}</div>;
}

function Section({ title, subtitle, children, testid, danger }) {
  return (
    <div className={`mt-8 border ${danger ? "border-blaze/50" : "border-neutral-800"} bg-[#080A0F] p-5 md:p-6`} data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className={`heading text-xl tracking-wider ${danger ? "text-blaze" : "text-white"}`}>{title.toUpperCase()}</h2>
          {subtitle && <div className="text-neutral-500 text-xs mt-1">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function TextField({ label, v, onChange, tid, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label>{label}</Label>
      <input data-testid={tid} value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none" />
    </div>
  );
}

function NumberField({ label, v, onChange, tid, step }) {
  return (
    <div>
      <Label>{label}</Label>
      <input data-testid={tid} type="number" step={step || "1"} value={v ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none" />
    </div>
  );
}

function TextAreaField({ label, v, onChange, tid, rows, wide = true }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label>{label}</Label>
      <textarea data-testid={tid} rows={rows || 3} value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none" />
    </div>
  );
}

function BoolField({ label, v, onChange, tid }) {
  return (
    <label className="flex items-center gap-2 text-sm bg-black border border-neutral-800 px-3 py-2 hover:border-neutral-700 cursor-pointer">
      <input type="checkbox" data-testid={tid} checked={!!v} onChange={(e) => onChange(e.target.checked)} className="accent-volt" />
      <span className="text-neutral-300">{label}</span>
    </label>
  );
}

function SelectField({ label, v, options, onChange, tid }) {
  return (
    <div>
      <Label>{label}</Label>
      <select data-testid={tid} value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none">
        {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </div>
  );
}

function StringListField({ label, v, onChange, tid, wide }) {
  const joined = Array.isArray(v) ? v.join(", ") : "";
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label>{label} <span className="text-neutral-600">(comma-separated)</span></Label>
      <input data-testid={tid} value={joined} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none" />
    </div>
  );
}

function JsonField({ v, onChange, onError, onValid, tid, rows }) {
  const [text, setText] = useState(() => JSON.stringify(v ?? {}, null, 2));
  const [err, setErr] = useState(null);

  useEffect(() => {
    setText(JSON.stringify(v ?? {}, null, 2));
    setErr(null);
    onValid?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(v)]);

  const handle = (t) => {
    setText(t);
    try {
      const parsed = JSON.parse(t);
      setErr(null);
      onValid?.();
      onChange(parsed);
    } catch (e) {
      setErr(e.message);
      onError?.(e.message);
    }
  };

  return (
    <div>
      <textarea data-testid={tid} rows={rows || 10} value={text} onChange={(e) => handle(e.target.value)} spellCheck={false} className={`w-full font-mono text-xs bg-black border ${err ? "border-blaze" : "border-neutral-800"} px-3 py-2 focus:border-volt outline-none`} />
      {err && <div className="text-blaze text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} /> {err}</div>}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-[10px] tracking-widest text-neutral-500">{children}</label>;
}
